/**
 * Grid System Tokens - PROOF OF CONCEPT - NEEDS FURTHER TESTING AND IMPLEMENTATION - NOT READY FOR PRODUCTION
 *
 * 12-column grid system for consistent layouts across the app.
 * Based on Bootstrap/industry standard for designer familiarity.
 *
 * BREAKPOINT DEPENDENCY:
 * The responsive keys (base, sm, md, lg, xl, 2xl) used in gridSpans
 * are resolved by Chakra against breakpoints defined in:
 *   → breakpoints.ts → theme.ts → Chakra theme
 *
 * Current breakpoints (from breakpoints.ts):
 *   base: 0px      (mobile phones)
 *   xs:   576px    (large phones)
 *   sm:   768px    (tablets)
 *   md:   1024px   (laptops)
 *   lg:   1200px   (desktops) ← LAYOUT SWITCH POINT (8+4 split)
 *   xl:   1440px   (wide screens)
 *   2xl:  1600px   (ultra-wide)
 *
 * Usage:
 *   import { GRID, gridTemplates } from "@shared/theme/props/grid";
 *
 *   <Grid templateColumns={gridTemplates.default} gap={GRID.gaps.default}>
 *     <GridItem colSpan={GRID.spans.main}>Main content</GridItem>
 *     <GridItem colSpan={GRID.spans.sidebar}>Sidebar</GridItem>
 *   </Grid>
 */

// =============================================================================
// GRID CONSTANTS
// =============================================================================

/** Standard column count */
export const GRID_COLUMNS = 12;

/** Default gap between grid items (uses spacing token) */
export const GRID_GAP_DEFAULT = "xl"; // 24px

// =============================================================================
// GRID TEMPLATES (for templateColumns prop)
// =============================================================================

export const gridTemplates = {
  /** Standard 12-column grid */
  default: `repeat(${GRID_COLUMNS}, 1fr)`,
  /** 2-column equal split */
  split: "repeat(2, 1fr)",
  /** 3-column equal */
  thirds: "repeat(3, 1fr)",
  /** 4-column equal */
  quarters: "repeat(4, 1fr)",
  /** 6-column equal */
  sixths: "repeat(6, 1fr)",
} as const;

// =============================================================================
// RESPONSIVE COLUMN SPANS
// =============================================================================

/**
 * Responsive column span presets
 *
 * These use Chakra's responsive object syntax:
 *   { base: 12, lg: 8 } means:
 *   - base (0-1199px): 12 columns (full width, stacked)
 *   - lg (1200px+): 8 columns (side-by-side)
 *
 * Layout switch points:
 *   - base (0-1199px): Mobile/Tablet - everything stacks (full width)
 *   - lg (1200px+): Desktop - side-by-side layouts begin
 *
 * Common patterns:
 *   Mobile/Tablet (base): Full width (12) stacked
 *   Desktop (lg+):        Side-by-side (8+4, 9+3, etc.)
 */
export const gridSpans = {
  // === FULL WIDTH ===
  /** Full width - always 12 columns */
  full: { base: 12 },

  // === MAIN + SIDEBAR (8/4 split at desktop) ===
  /** Main content: 12 mobile/tablet → 8 desktop (66.67%) */
  main: { base: 12, lg: 8 },
  /** Sidebar: 12 mobile/tablet → 4 desktop (33.33%) */
  sidebar: { base: 12, lg: 4 },

  // === HEADER LAYOUT (flips at desktop) ===
  /** Header image: 12 mobile → 8 tablet → 4 desktop (image shrinks at lg) */
  headerImage: { base: 12, sm: 4, lg: 8 },
  /** Header details: 12 mobile → 4 tablet → 8 desktop (details grow at lg) */
  headerDetails: { base: 12, sm: 8, lg: 4 },

  // === WIDE MAIN + NARROW SIDEBAR (9/3 split at desktop) ===
  /** Wide main: 12 mobile/tablet → 9 desktop (75%) */
  mainWide: { base: 12, lg: 9 },
  /** Narrow sidebar: 12 mobile/tablet → 3 desktop (25%) */
  sidebarNarrow: { base: 12, lg: 3 },

  // === EQUAL SPLIT (6/6 at tablet+) ===
  /** Half width: 12 mobile → 6 tablet+ (50%) */
  half: { base: 12, sm: 6 },

  // === THIRDS (4/4/4 at desktop) ===
  /** Third width: 12 mobile → 6 tablet → 4 desktop (33.33%) */
  third: { base: 12, sm: 6, lg: 4 },

  // === QUARTERS (3/3/3/3 at desktop) ===
  /** Quarter width: 12 mobile → 6 tablet → 3 desktop (25%) */
  quarter: { base: 12, sm: 6, lg: 3 },

  // === CENTERED CONTENT (2/8/2 at desktop) ===
  /** Centered main: 12 mobile/tablet → 8 desktop */
  centeredMain: { base: 12, lg: 8 },
  /** Offset for centered layout (colStart) */
  centeredOffset: { base: 1, lg: 3 },
} as const;

// =============================================================================
// GAP PRESETS
// =============================================================================

export const gridGaps = {
  /** No gap */
  none: "none",
  /** Compact gap (16px) */
  compact: "md",
  /** Default gap (24px) */
  default: "xl",
  /** Spacious gap (32px) */
  spacious: "2xl",
  /** Large gap (40px) */
  large: "3xl",
} as const;

// =============================================================================
// COMBINED EXPORT
// =============================================================================

/**
 * Grid configuration object
 *
 * Usage:
 *   import { GRID } from "@shared/theme/props/grid";
 *
 *   <Grid templateColumns={GRID.templates.default} gap={GRID.gaps.default}>
 *     <GridItem colSpan={GRID.spans.main}>Main</GridItem>
 *     <GridItem colSpan={GRID.spans.sidebar}>Sidebar</GridItem>
 *   </Grid>
 */
export const GRID = {
  columns: GRID_COLUMNS,
  templates: gridTemplates,
  spans: gridSpans,
  gaps: gridGaps,
} as const;

export default GRID;
